/**
 * A filter step's stack, fetched from `user-sections/:id/filter/:type`
 * (design §2). The fetch runs from a watch, never from inside a computed.
 *
 * P4-5: the GET fires as soon as the user section, the step documentId and
 * the filtered drill type (`documentStepRepo.type`) are known — it does not
 * wait for the drill or the user step, so it runs in parallel with the step
 * GET when the section seeded the step (useSectionStepSeed). The backend
 * derives `number_of_steps_back` from `step_doc_id`; it is still sent when the
 * drill is already loaded. One GET per open: an in-flight or answered fetch is
 * never repeated; only a failed one is retried when the step or user step
 * changes.
 *
 * The answer is published (`filterStackSource`, `isFilterStackSourceFetched`,
 * then `onLoaded(items)` synchronously) only once the user step is in the
 * repo, as before: `onLoaded` (useStep's skipped-filter recorder) needs it for
 * the completed-step guard, and a "fetched" flag without the skip recorded
 * would let the INSTRUCTIONS dialog open on a skipped filter. `onLoaded` runs
 * for every successful fetch; the skip condition (`items.length === 0 &&
 * !completion_date`) is the callback's to check.
 */
import { computed, nextTick, ref, watch, type Ref } from 'vue';
import { useStrapiGet } from 'src/shared/composables/useStrapiApi';
import type { StackItems } from '../utilities/stackItems';

export interface UseFilterStackSourceOptions {
  /** The step's documentId (sent as `step_doc_id`). */
  documentId: string;
  /** Without a user section there is nothing to filter. */
  userSectionDocId?: string;
  /** Kebab step type ('filter' for a filter step). */
  stepType: Readonly<Ref<string | null>>;
  /** Not needed to fetch any more (P4-5); kept in the options shape. */
  stepDrill: Readonly<Ref<unknown>>;
  filterSettings: Readonly<
    Ref<{ number_of_steps_back?: number | null } | null | undefined>
  >;
  /** The step (seeded or loaded); its `type` is the filtered drill type. Watched. */
  documentStepRepo: Readonly<Ref<{ type: string } | null | undefined>>;
  repoUserStep: Readonly<Ref<{ id?: number } | null | undefined>>;
  /** Runs right after a successful fetch assigned its items. */
  onLoaded: (items: StackItems) => void;
}

/** A finished fetch waiting for the user step: the items, or null on failure. */
type Outcome = { items: StackItems | null };

export function useFilterStackSource({
  documentId,
  userSectionDocId,
  stepType,
  filterSettings,
  documentStepRepo,
  repoUserStep,
  onLoaded,
}: UseFilterStackSourceOptions) {
  const isFilterStackSource = computed(() => {
    return stepType.value === 'filter';
  });

  const filterStackSource = ref<StackItems | null>(null);
  const isFilterStackSourceLoading = ref(false);
  const isFilterStackSourceFetched = ref(false);
  /** Set when a fetch answered with items: no further fetch this open. */
  let hasItems = false;
  let pending: Outcome | null = null;

  function publish() {
    if (!pending || !repoUserStep.value) return;
    const { items } = pending;
    pending = null;
    filterStackSource.value = items;
    isFilterStackSourceFetched.value = true;
    if (items) onLoaded(items);
  }

  const getFilterStackSource = async () => {
    if (
      !userSectionDocId ||
      hasItems ||
      pending ||
      isFilterStackSourceLoading.value ||
      !isFilterStackSource.value
    )
      return;

    const filterType = documentStepRepo.value?.type;
    if (!filterType) return;

    // 4. get the stack by the filter type from strapi
    isFilterStackSourceLoading.value = true;

    const number_of_steps_back = filterSettings.value?.number_of_steps_back;

    const params: { number_of_steps_back?: number; step_doc_id?: string } =
      number_of_steps_back && number_of_steps_back > 0
        ? { number_of_steps_back, step_doc_id: documentId }
        : { step_doc_id: documentId };

    const { execute, data } = useStrapiGet(
      `user-sections/${userSectionDocId}/filter/${filterType}`,
      params,
    );

    let items: StackItems | null = null;
    try {
      await execute();
      items = (data.value as StackItems | null | undefined) ?? null;
    } catch (error) {
      console.error('filterStepStack:', error);
    } finally {
      isFilterStackSourceLoading.value = false;
    }

    hasItems = !!items;
    pending = { items };
    publish();
  };

  // The filter stack is fetched by this watch, never from inside a computed.
  // A failed fetch is retried when the step or user-step changes.
  watch(
    [isFilterStackSource, documentStepRepo, () => repoUserStep.value?.id],
    ([isFilter]) => {
      if (!isFilter) return;
      publish();
      void nextTick(() => void getFilterStackSource());
    },
    { immediate: true },
  );

  return {
    filterStackSource,
    isFilterStackSource,
    isFilterStackSourceFetched,
    isFilterStackSourceLoading,
  };
}

export type UseFilterStackSourceReturn = ReturnType<
  typeof useFilterStackSource
>;
